import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Star,
  Building2,
  RefreshCw,
  Factory,
  Package,
  Truck,
  Wrench,
  ExternalLink,
  MapPin,
  Navigation2,
  Navigation,
  ChevronLeft,
  ChevronRight,
  Map as MapIcon,
} from 'lucide-react';
import SupplyChainMap from '../Maps/SupplyChainMap';
import RouteDetailsDialog from './RouteDetailsDialog';
import WatchlistTable from './WatchlistTable';
import CompanyConnectionsGraph from './CompanyConnectionsGraph';
import { theme } from '../../config/theme';
import { getGoogleMapsRouteUrl } from '../../utils/geocoding';
import { colors } from '../../config/colors';
import { spacing } from '../../config/spacing';
import { typography } from '../../config/typography';
import { Card } from '../UI/card';
import { Button } from '../UI/button';
import { useLocale } from '../../contexts/LocaleContext';

function WatchlistListLayout({
  watchlist = [],
  companies = [],
  cardStyle,
  pageSize,
  kanbanColumns,
  kanbanGroups,
  kanbanDraggedCompanyId,
  kanbanDropTargetId,
  kanbanUpdating,
  onKanbanDragStart,
  onKanbanDragEnd,
  onKanbanDragOver,
  onKanbanDragLeave,
  onKanbanDrop,
  companyById,
  getRiskLevel,
  markers,
  routes,
  bounds,
  geocoding,
  onMapRefresh,
  allRoutesWithItem,
  selectedRouteIndex,
  onSelectedRouteIndexChange,
  addressMap,
  showRouteDetailsForRouteId,
  onShowRouteDetailsForRouteId,
  routeAnalyses,
  analyzingRoutes,
  onAnalyzeRoute,
  onAddCompany,
}) {
  const navigate = useNavigate();
  const { t } = useLocale();
  const cardTitleStyle = typography.textStyles?.cardTitle || typography.textStyles?.h2 || {};

  return (
    <div className="space-y-6" style={{ gap: spacing.scale?.[6] ?? 24 }}>
      <WatchlistTable
        watchlist={watchlist}
        companies={companies}
        title={t('watchlistTable')}
        emptyMessage={t('noRecentActivity')}
        pageSize={pageSize}
        cardStyle={cardStyle}
        showPriorityFilter
      />

      <div>
        <h2
          className="font-semibold mb-4"
          style={{ color: colors.text.primary, ...cardTitleStyle }}
        >
          {t('byCompanyType')}
        </h2>
        {watchlist.length === 0 ? (
          <Card style={cardStyle} className="p-8 text-center">
            <Star className="h-12 w-12 mx-auto mb-3" style={{ color: colors.text.tertiary }} />
            <p className="text-sm mb-4" style={{ color: colors.text.secondary }}>
              {t('addCompaniesToWatchlist')}
            </p>
            <Button variant="default" onClick={onAddCompany}>
              <Plus className="h-4 w-4 mr-2" />
              {t('addCompany')}
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 min-w-0">
            {kanbanColumns.map((col) => {
              const Icon = col.icon;
              const items = kanbanGroups[col.id] || [];
              const headerPad = spacing.scale?.[2] ?? 12;
              const columnPad = 10;
              const cardPad = 10;
              return (
                <Card
                  key={col.id}
                  style={{
                    ...cardStyle,
                    borderTopWidth: 3,
                    borderTopColor: col.color,
                    minWidth: 200,
                    padding: 0,
                  }}
                  className="flex flex-col overflow-hidden !p-0"
                >
                  <div
                    className="border-b flex items-center gap-2 flex-shrink-0"
                    style={{ borderColor: colors.border.light, padding: headerPad }}
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${col.color}20`, color: col.color }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <span
                      className="font-medium text-sm truncate"
                      style={{ color: colors.text.primary }}
                    >
                      {t(col.labelKey)}
                    </span>
                    <span
                      className="text-xs ml-auto flex-shrink-0"
                      style={{ color: colors.text.tertiary }}
                    >
                      {items.length}
                    </span>
                  </div>
                  <div
                    className="space-y-2 flex-1 min-h-0 overflow-y-auto max-h-[420px] transition-colors"
                    style={{
                      padding: columnPad,
                      minHeight: 280,
                      outline: kanbanDropTargetId === col.id ? `2px dashed ${col.color}` : 'none',
                      outlineOffset: -1,
                      borderRadius: spacing.radius?.md ?? 8,
                      backgroundColor: kanbanDropTargetId === col.id ? `${col.color}12` : undefined,
                    }}
                    onDragOver={(e) => onKanbanDragOver(e, col.id)}
                    onDragLeave={onKanbanDragLeave}
                    onDrop={(e) => onKanbanDrop(e, col.id)}
                  >
                    {items.map((item) => {
                      const company = companyById[item.companyId];
                      const name = company?.name || item.companyId;
                      const riskLevelKey = getRiskLevel(item);
                      const riskColor =
                        riskLevelKey === 'riskLevelHigh'
                          ? colors.danger[500]
                          : riskLevelKey === 'riskLevelLow'
                            ? colors.success[500]
                            : colors.warning[500];
                      const routeCount = item.userCompanyInfo?.routes?.length ?? 0;
                      const isDragging = kanbanDraggedCompanyId === item.companyId;
                      return (
                        <div
                          key={item.companyId}
                          draggable={!kanbanUpdating}
                          onDragStart={(e) => onKanbanDragStart(e, item)}
                          onDragEnd={onKanbanDragEnd}
                          className={`rounded-lg border transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${!kanbanUpdating ? 'cursor-grab active:cursor-grabbing' : ''}`}
                          style={{
                            padding: cardPad,
                            minHeight: 76,
                            borderColor: colors.border.light,
                            backgroundColor: colors.background.secondary,
                            opacity: isDragging ? 0.5 : 1,
                          }}
                        >
                          <button
                            type="button"
                            className="block w-full text-left font-medium text-sm truncate hover:underline focus:outline-none"
                            style={{ color: colors.primary[600] }}
                            onClick={() => navigate(`/companies/${item.companyId}`)}
                          >
                            {name}
                          </button>
                          <div className="flex items-center gap-2 mt-2">
                            <span
                              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                              style={{ backgroundColor: riskColor }}
                              title={t(riskLevelKey)}
                            />
                            <span className="text-xs" style={{ color: colors.text.secondary }}>
                              {t(riskLevelKey)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-xs" style={{ color: colors.text.tertiary }}>
                            <Navigation2 className="h-3 w-3 flex-shrink-0" />
                            <span>
                              {routeCount} {t('kanbanRoutes')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {watchlist.length > 0 ? (
        <div className="w-full">
          <CompanyConnectionsGraph watchlist={watchlist} companies={companies} />
        </div>
      ) : null}

      {watchlist.length > 0 ? (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
            <div className="lg:col-span-2 flex flex-col">
              <Card style={cardStyle} className="p-6 flex flex-col h-[400px]">
                <div className="flex items-center justify-between mb-4">
                  <h3
                    className="font-semibold"
                    style={{ color: colors.text.primary, ...cardTitleStyle }}
                  >
                    {t('globalSentimentAndLocations')}
                  </h3>
                  <button
                    type="button"
                    onClick={onMapRefresh}
                    className="p-2 rounded-lg transition-colors hover:opacity-80"
                    style={{ color: colors.text.secondary }}
                    aria-label={t('refreshData')}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
                <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg">
                  <SupplyChainMap
                    markers={markers}
                    routes={routes}
                    bounds={bounds}
                    geocoding={geocoding}
                    compact
                    minHeight={320}
                  />
                </div>
              </Card>
            </div>
            <div className="flex flex-col h-[400px] min-h-0">
              <Card style={cardStyle} className="p-4 flex flex-col h-full min-h-0">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                    {t('routesTitle')}
                  </h3>
                  {allRoutesWithItem.length > 1 ? (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() => onSelectedRouteIndexChange((i) => Math.max(0, i - 1))}
                        disabled={selectedRouteIndex <= 0}
                        aria-label={t('previousRoute')}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        onClick={() =>
                          onSelectedRouteIndexChange((i) =>
                            Math.min(allRoutesWithItem.length - 1, i + 1)
                          )
                        }
                        disabled={selectedRouteIndex >= allRoutesWithItem.length - 1}
                        aria-label={t('nextRoute')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </div>
                {allRoutesWithItem.length === 0 ? (
                  <div className="flex-1 flex items-center justify-center text-center p-6">
                    <p className="text-sm" style={{ color: colors.text.secondary }}>
                      {t('noRoutesDefined')}
                    </p>
                  </div>
                ) : (() => {
                  const { route } = allRoutesWithItem[selectedRouteIndex] || {};
                  if (!route) return null;
                  const rid = route.routeId ?? route.route_id;
                  const startId = route.startPointAddressId ?? route.start_point_address_id;
                  const endId = route.endPointAddressId ?? route.end_point_address_id;
                  const startAddr = startId != null ? addressMap[String(startId)] : null;
                  const endAddr = endId != null ? addressMap[String(endId)] : null;
                  const startLine = startAddr
                    ? [
                        startAddr.streetAddress ?? startAddr.street_address,
                        startAddr.city,
                        startAddr.stateProvince ?? startAddr.state_province,
                        startAddr.country,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : t('unknownAddress');
                  const endLine = endAddr
                    ? [
                        endAddr.streetAddress ?? endAddr.street_address,
                        endAddr.city,
                        endAddr.stateProvince ?? endAddr.state_province,
                        endAddr.country,
                      ]
                        .filter(Boolean)
                        .join(', ')
                    : t('unknownAddress');
                  const startShort = startLine.length > 40 ? `${startLine.slice(0, 37)}…` : startLine;
                  const endShort = endLine.length > 40 ? `${endLine.slice(0, 37)}…` : endLine;
                  return (
                    <div className="flex-1 min-h-0 overflow-y-auto">
                      <div
                        className="rounded-xl border-2 overflow-hidden"
                        style={{
                          borderColor: route.isActive !== false ? colors.success[300] : colors.gray[300],
                          backgroundColor: route.isActive !== false ? colors.success[50] : colors.gray[50],
                        }}
                      >
                        <div
                          className="p-4 flex items-start justify-between"
                          style={{
                            backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100],
                          }}
                        >
                          <div className="flex items-start space-x-3 flex-1 min-w-0">
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                              style={{
                                backgroundColor: route.isActive !== false ? colors.success[500] : colors.gray[500],
                              }}
                            >
                              <Navigation className="h-5 w-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3
                                className="font-semibold text-sm truncate"
                                style={{ color: colors.text.primary }}
                              >
                                {route.routeName ?? route.route_name ?? t('routesTitle')}
                              </h3>
                            </div>
                          </div>
                          <span
                            className="px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2"
                            style={{
                              backgroundColor: route.isActive !== false ? colors.success[200] : colors.gray[200],
                              color: route.isActive !== false ? colors.success[700] : colors.gray[700],
                            }}
                          >
                            {route.isActive !== false ? t('active') : t('routeDetailsInactive')}
                          </span>
                        </div>
                        <div className="p-4 space-y-3">
                          <div className="flex items-start space-x-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.success[200] }}
                            >
                              <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>
                                {t('routeDetailsStartingPoint')}
                              </p>
                              <p
                                className="text-sm font-semibold mt-0.5 truncate"
                                style={{ color: colors.text.primary }}
                              >
                                {startShort}
                              </p>
                              {startLine !== startShort ? (
                                <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>
                                  {startLine}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="flex items-center justify-center">
                            <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                            <div className="mx-2 px-2 py-1 rounded-full" style={{ backgroundColor: colors.gray[200] }}>
                              <span className="text-xs font-bold" style={{ color: colors.text.secondary }}>
                                →
                              </span>
                            </div>
                            <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                          </div>
                          <div className="flex items-start space-x-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.danger[200] }}
                            >
                              <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>
                                {t('routeDetailsDestination')}
                              </p>
                              <p
                                className="text-sm font-semibold mt-0.5 truncate"
                                style={{ color: colors.text.primary }}
                              >
                                {endShort}
                              </p>
                              {endLine !== endShort ? (
                                <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>
                                  {endLine}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <Button
                            className="w-full mt-3"
                            size="sm"
                            onClick={() => onShowRouteDetailsForRouteId(rid)}
                            style={{ backgroundColor: colors.primary[500], color: colors.text.inverse }}
                          >
                            <MapIcon className="h-4 w-4 mr-2" />
                            {t('routeDetailsViewAnalysis')}
                            <ExternalLink className="h-3 w-3 ml-2" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </Card>
            </div>
          </div>

          {showRouteDetailsForRouteId != null
            ? (() => {
                const routeId = showRouteDetailsForRouteId;
                const analysis = routeAnalyses[routeId];
                const pair = allRoutesWithItem.find(
                  (x) => (x.route.routeId ?? x.route.route_id) === routeId
                );
                const route = pair?.route;
                const startId = route?.startPointAddressId ?? route?.start_point_address_id;
                const endId = route?.endPointAddressId ?? route?.end_point_address_id;
                const startAddr = startId != null ? addressMap[String(startId)] : null;
                const endAddr = endId != null ? addressMap[String(endId)] : null;
                const startLine = startAddr
                  ? [
                      startAddr.streetAddress ?? startAddr.street_address,
                      startAddr.city,
                      startAddr.stateProvince ?? startAddr.state_province,
                      startAddr.country,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : '';
                const endLine = endAddr
                  ? [
                      endAddr.streetAddress ?? endAddr.street_address,
                      endAddr.city,
                      endAddr.stateProvince ?? endAddr.state_province,
                      endAddr.country,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  : '';
                const mapsUrl = startLine && endLine ? getGoogleMapsRouteUrl(startLine, endLine) : null;
                if (analysis) {
                  return (
                    <RouteDetailsDialog
                      routeAnalysis={analysis}
                      onClose={() => onShowRouteDetailsForRouteId(null)}
                      onAnalyze={() => onAnalyzeRoute(routeId)}
                      analyzing={!!analyzingRoutes[routeId]}
                    />
                  );
                }
                if (!route) return null;
                return (
                  <>
                    <div
                      className="fixed inset-0 transition-opacity"
                      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: theme.zIndex.overlay }}
                      onClick={() => onShowRouteDetailsForRouteId(null)}
                      aria-hidden
                    />
                    <div
                      className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-xl shadow-xl flex flex-col"
                      style={{
                        backgroundColor: colors.background.card,
                        zIndex: theme.zIndex.modal,
                        border: `1px solid ${colors.border.light}`,
                      }}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="route-details-title"
                    >
                      <div
                        className="flex items-center justify-between flex-shrink-0 px-5 py-4 border-b"
                        style={{ borderColor: colors.border.light }}
                      >
                        <h2
                          id="route-details-title"
                          className="text-lg font-semibold"
                          style={{ color: colors.text.primary }}
                        >
                          {t('routeDetailsTitle')}
                        </h2>
                        <button
                          type="button"
                          onClick={() => onShowRouteDetailsForRouteId(null)}
                          className="p-2 rounded-lg transition-colors hover:opacity-80"
                          style={{ color: colors.text.secondary }}
                          aria-label={t('close')}
                        >
                          <span className="text-xl leading-none">×</span>
                        </button>
                      </div>
                      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                            style={{
                              backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100],
                            }}
                          >
                            <Navigation
                              className="h-5 w-5"
                              style={{
                                color: route.isActive !== false ? colors.success[600] : colors.gray[600],
                              }}
                            />
                          </div>
                          <div>
                            <h3 className="font-semibold text-base" style={{ color: colors.text.primary }}>
                              {route.routeName ?? route.route_name ?? t('routesTitle')}
                            </h3>
                            <span
                              className="text-xs font-medium px-2 py-0.5 rounded"
                              style={{
                                backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100],
                                color: route.isActive !== false ? colors.success[700] : colors.gray[600],
                              }}
                            >
                              {route.isActive !== false ? t('active') : t('routeDetailsInactive')}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div className="flex gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.success[100] }}
                            >
                              <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>
                                {t('routeDetailsStartingPoint')}
                              </p>
                              <p className="text-sm font-medium mt-0.5 truncate" style={{ color: colors.text.primary }}>
                                {startLine || '—'}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: colors.danger[100] }}
                            >
                              <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>
                                {t('routeDetailsDestination')}
                              </p>
                              <p className="text-sm font-medium mt-0.5 truncate" style={{ color: colors.text.primary }}>
                                {endLine || '—'}
                              </p>
                            </div>
                          </div>
                        </div>
                        <p className="text-sm" style={{ color: colors.text.secondary }}>
                          {t('routeDetailsViewAnalysisNoData')}
                        </p>
                      </div>
                      <div
                        className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0 px-5 py-4 border-t"
                        style={{ borderColor: colors.border.light, backgroundColor: colors.gray[50] }}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onAnalyzeRoute(routeId)}
                          disabled={!!analyzingRoutes[routeId]}
                        >
                          {analyzingRoutes[routeId]
                            ? t('routeDetailsAnalyzing')
                            : t('routeDetailsRefreshAnalysis')}
                        </Button>
                        {mapsUrl ? (
                          <Button
                            size="sm"
                            onClick={() => window.open(mapsUrl, '_blank')}
                            style={{ backgroundColor: colors.primary[500], color: colors.text.inverse }}
                          >
                            <ExternalLink className="h-4 w-4 mr-1.5" />
                            {t('routeDetailsViewInMaps')}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </>
                );
              })()
            : null}
        </>
      ) : null}
    </div>
  );
}

export default WatchlistListLayout;
